import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Tab, Tabs } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import JoinInnerOutlinedIcon from "@mui/icons-material/JoinInnerOutlined";
import { LocationCard } from "../../Common/LocationCard";
import { removeDuplicateLocations } from "../../../helper/address";
import { CompanyPropTypes } from "../../../schema/company";
import CompanyMetricsPanelV2 from "./CompanyMetricsPanelV2";
import ClassificationCardV2 from "./ClassificationCardV2";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PeopleTableV2 from "./PeopleTableV2";
import AiEnrichmentHistoryTable from "./AiEnrichmentHistoryTable";
import SimilarCompaniesPanelV2 from "./SimilarCompaniesPanelV2";
import CompanySidebarEmptyState from "./CompanySidebarEmptyState";
import { HEADER } from "./headerTokens";
import { SIDEBAR } from "./sidebarVisual";
import {
  ReadOnlyBlock,
  SidebarLabel,
  SidebarPanel,
  SidebarSection,
  SidebarSectionTitle,
  SidebarStack,
} from "./SidebarPrimitives";

import { AI_ENRICHMENT_TAB_INDEX } from "./companyEnrichmentFormat";

const TABS = [
  { id: "metrics", label: "Metrics", Icon: InsightsOutlinedIcon },
  { id: "details", label: "Details", Icon: InfoOutlinedIcon },
  { id: "location", label: "Location", Icon: ExploreOutlinedIcon },
  { id: "people", label: "People", Icon: PeopleAltOutlinedIcon },
  { id: "similar", label: "Similar", Icon: JoinInnerOutlinedIcon },
  { id: "aiEnrichment", label: "AI Enrichment", Icon: AutoAwesomeOutlinedIcon },
];

const HISTORY_LOADING_ENTRIES = [{
  id: "history-loading",
  prompt: "Loading history",
  status: "running",
  actionType: "ai_enrichment",
  resultPayload: { status: "running", actionType: "ai_enrichment" },
}];

export { AI_ENRICHMENT_TAB_INDEX };

function DetailsPanel({ companyDetail, compact = false }) {
  const hasClassification =
    Boolean(companyDetail?.sourcedClassifications?.length) ||
    Boolean(companyDetail?.modeledClassifications?.length);

  if (!hasClassification) {
    return (
      <SidebarPanel compact={compact}>
        <CompanySidebarEmptyState variant="details" testId="details-tab-empty-state" compact={compact} />
      </SidebarPanel>
    );
  }

  return (
    <SidebarPanel compact={compact}>
      <SidebarSection title="Classification" showDivider={false}>
        <SidebarLabel>From online sources</SidebarLabel>
        {companyDetail?.sourcedClassifications?.length ? (
          <SidebarStack sx={{ mb: SIDEBAR.gap.listItem }}>
            {companyDetail.sourcedClassifications.map((classification, index) => (
              <ClassificationCardV2
                key={`sourced-${index}`}
                levels={classification.levels || classification}
              />
            ))}
          </SidebarStack>
        ) : (
          <ReadOnlyBlock testId="details-sourced-empty" sx={{ mb: SIDEBAR.gap.listItem }}>
            No sourced classifications
          </ReadOnlyBlock>
        )}
        <SidebarLabel sx={{ mt: SIDEBAR.gap.withinCard }}>AlphaSearch AI</SidebarLabel>
        {companyDetail?.modeledClassifications?.length ? (
          <SidebarStack>
            {companyDetail.modeledClassifications.map((classification, index) => (
              <ClassificationCardV2
                key={`modeled-${index}`}
                levels={classification.levels || classification}
              />
            ))}
          </SidebarStack>
        ) : (
          <ReadOnlyBlock testId="details-modeled-empty">No modeled classifications</ReadOnlyBlock>
        )}
      </SidebarSection>
    </SidebarPanel>
  );
}

DetailsPanel.propTypes = {
  companyDetail: CompanyPropTypes,
  compact: PropTypes.bool,
};

export default function CompanyDetailTabsV2({
  companyDetail,
  compact = false,
  activeTab,
  onTabChange,
  enrichmentHistory = [],
  enrichmentHistoryError,
  enrichmentHistoryLoading = false,
  onRequestOpenActions,
}) {
  const [internalTab, setInternalTab] = useState(0);
  const tab = activeTab ?? internalTab;
  const setTab = onTabChange ?? setInternalTab;
  const tabMinHeight = compact ? 48 : 40;
  const primaryMapHeight = compact ? 220 : 260;
  const secondaryMapHeight = compact ? 180 : 180;

  let otherLocations = null;
  if (companyDetail?.otherLocations?.length > 0) {
    otherLocations = removeDuplicateLocations(
      companyDetail?.primaryLocation,
      companyDetail?.otherLocations
    );
  }

  const people = (companyDetail?.people || []).filter(
    (person) => person && (person.fullName || person.name || person.email || person.title)
  );

  return (
    <Box
      data-testid="company-detail-tabs-v2"
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Tabs
        value={tab}
        onChange={(_event, next) => setTab(next)}
        variant="scrollable"
        scrollButtons={compact ? "auto" : false}
        allowScrollButtonsMobile={compact}
        aria-label="Company detail sections"
        TabIndicatorProps={{
          sx: {
            height: HEADER.tabIndicator.height,
            borderRadius: `${HEADER.tabIndicator.height}px ${HEADER.tabIndicator.height}px 0 0`,
            backgroundColor: "primary.main",
          },
        }}
        sx={{
          flexShrink: 0,
          position: compact ? "sticky" : "relative",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          minHeight: tabMinHeight,
          borderBottom: `${HEADER.tabRail.height}px solid ${HEADER.tabRail.color}`,
          "& .MuiTabs-flexContainer": {
            gap: SIDEBAR.gap.withinCard,
          },
          "& .MuiTabs-indicator": {
            bottom: 0,
          },
          "& .MuiTab-root": {
            textTransform: "none",
            minHeight: tabMinHeight,
            minWidth: compact ? 72 : "auto",
            px: compact ? 1 : 0.75,
            py: 0.75,
            color: "text.secondary",
            fontSize: compact ? 12 : 12.5,
            fontWeight: 500,
            opacity: 1,
            gap: 0.55,
            lineHeight: 1.2,
          },
          "& .MuiTab-root .MuiSvgIcon-root": {
            fontSize: compact ? 16 : 15,
            color: "inherit",
            marginBottom: "0 !important",
            marginRight: "0 !important",
          },
          "& .MuiTab-iconWrapper": {
            marginRight: "0 !important",
            marginBottom: "0 !important",
          },
          "& .Mui-selected": {
            color: "primary.main",
            fontWeight: 600,
          },
          "& .Mui-selected .MuiSvgIcon-root": {
            color: "primary.main",
          },
        }}
      >
        {TABS.map(({ id, label, Icon }) => (
          <Tab
            key={id}
            id={`company-v2-tab-${id}`}
            icon={<Icon />}
            iconPosition="start"
            label={label}
            disableRipple
          />
        ))}
      </Tabs>

      {tab === 0 && (
        <SidebarPanel compact={compact}>
          <CompanyMetricsPanelV2
            companyDetail={companyDetail}
            compact={compact}
            showTitle={!compact}
            summaryCardMuted
          />
        </SidebarPanel>
      )}

      {tab === 1 && <DetailsPanel companyDetail={companyDetail} compact={compact} />}

      {tab === 2 && (
        <SidebarPanel compact={compact}>
          {companyDetail?.primaryLocation ? (
            <LocationCard
              location={companyDetail.primaryLocation}
              variant="panel"
              showSectionTitle
              mapHeight={primaryMapHeight}
              mb={otherLocations?.length ? SIDEBAR.gap.card : 0}
            />
          ) : (
            <CompanySidebarEmptyState
              variant="location"
              testId="location-tab-empty-state"
              compact={compact}
            />
          )}
          {otherLocations?.length > 0 ? (
            <Box sx={{ mt: companyDetail?.primaryLocation ? SIDEBAR.gap.withinCard : 0 }}>
              <SidebarSectionTitle>Other addresses</SidebarSectionTitle>
              <SidebarStack>
                {otherLocations.map((address) => (
                  <LocationCard
                    key={
                      address.streetAddress ||
                      address.fullAddress ||
                      address.city
                    }
                    location={address}
                    variant="panel"
                    mapHeight={secondaryMapHeight}
                    mb={0}
                  />
                ))}
              </SidebarStack>
            </Box>
          ) : null}
        </SidebarPanel>
      )}

      {tab === 3 && (
        <SidebarPanel compact={compact}>
          {people.length ? (
            <PeopleTableV2 people={people} compact={compact} />
          ) : (
            <CompanySidebarEmptyState
              variant="people"
              testId="people-tab-empty-state"
              compact={compact}
            />
          )}
        </SidebarPanel>
      )}

      {tab === 4 && (
        <SidebarPanel compact={compact}>
          <SimilarCompaniesPanelV2 ids={companyDetail?.similarCompanies || []} compact={compact} />
        </SidebarPanel>
      )}

      {tab === AI_ENRICHMENT_TAB_INDEX && (
        <SidebarPanel compact={compact}>
          {enrichmentHistoryError ? (
            <Box
              role="alert"
              sx={{ color: "text.secondary", fontSize: 13, py: 1 }}
            >
              {enrichmentHistoryError}
              {enrichmentHistory.length ? " Showing the last available history." : ""}
            </Box>
          ) : null}
          {enrichmentHistory.length ? (
            <AiEnrichmentHistoryTable entries={enrichmentHistory} compact={compact} />
          ) : enrichmentHistoryLoading ? (
            <AiEnrichmentHistoryTable entries={HISTORY_LOADING_ENTRIES} compact={compact} />
          ) : enrichmentHistoryError ? (
            null
          ) : (
            <CompanySidebarEmptyState
              variant="aiEnrichment"
              testId="ai-enrichment-tab-empty-state"
              compact={compact}
              actionLabel="Run an action"
              onAction={onRequestOpenActions}
            />
          )}
        </SidebarPanel>
      )}
    </Box>
  );
}

CompanyDetailTabsV2.propTypes = {
  companyDetail: CompanyPropTypes,
  compact: PropTypes.bool,
  activeTab: PropTypes.number,
  onTabChange: PropTypes.func,
  enrichmentHistory: PropTypes.arrayOf(PropTypes.object),
  enrichmentHistoryError: PropTypes.string,
  enrichmentHistoryLoading: PropTypes.bool,
  onRequestOpenActions: PropTypes.func,
};
